import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useCookies } from '@/utils/cookies';
import mixpanel from '@/mixpanel.ts';
import type { WidgetObject } from '@/types/Widget';
import type { UserData } from '@/types/User';
import { logOutApi } from '@/utils/api';

export const useUserStore = defineStore('user', () => {
  const cookies = useCookies();

  const accessToken = ref('');
  const userData = ref<UserData>();
  const demoEvent = ref();
  const demoTemplate = ref();

  type logInData = {
    token: string;
    user: UserData;
    expires_at: string;
    demo?: {
      event: {
        CampaignsEvent: {
          id: number;
          uuid: string;
          name: string;
        };
      };
      template?: {
        description?: string;
        global?: boolean;
        id?: number;
        name?: string;
        widgets?: WidgetObject[];
      };
    };
  };

  function logIn(response: logInData, short: boolean) {
    accessToken.value = response.token;
    userData.value = response.user;
    mixpanel.identify(userData.value.email);
    mixpanel.people.set({
      $email: userData.value.email,
      $name: userData.value.name,
      $role: userData.value.role_name,
      $account: userData.value.account_name,
      $account_id: userData.value.account_id,
    });
    mixpanel.track('Login');
    cookies
      .set('expiry', response.expires_at)
      .set('token', response.token)
      .set('user', userData.value)
      .set('short', short);

    if (response.demo) {
      localStorage.setItem('demoEvent', JSON.stringify(response.demo.event));
      localStorage.setItem('demoTemplate', JSON.stringify(response.demo.template));
      demoEvent.value = response.demo.event;
      demoTemplate.value = response.demo.template;
    }
  }

  function restoreFromCookies() {
    if (cookies?.get('user')) {
      accessToken.value = cookies.get('token');
      userData.value = cookies.get('user');

      if (localStorage.getItem('demoEvent')) {
        demoEvent.value = JSON.parse(`${localStorage.getItem('demoEvent')}`);
      }
      if (localStorage.getItem('demoTemplate')) {
        demoTemplate.value = JSON.parse(`${localStorage.getItem('demoTemplate')}`);
      }
      if (userData.value) {
        mixpanel.identify(userData.value.email);
      }
    }
  }

  function updateToken(token: string) {
    accessToken.value = token;
  }

  function getInitials() {
    if (userData.value && userData.value) {
      const { name } = userData.value;
      const namesSplit = name.split(' ');
      const init1 = Array.from(namesSplit[0])[0];
      let init2 = '';

      if (namesSplit[1]) {
        init2 = Array.from(namesSplit[1])[0];
      }

      return `${init1}${init2}`;
    }
    return '';
  }

  function getFirstName() {
    if (userData.value && userData.value) {
      const { name } = userData.value;
      const namesSplit = name.split(' ');
      return `${namesSplit[0]}`;
    }
    return '';
  }

  async function logOut() {
    mixpanel.track('Logout');
    delete userData.value;
    accessToken.value = '';

    await logOutApi(accessToken.value).catch((err) => {
      console.error(err);
    });
    cookies.remove('expiry');
    cookies.remove('token');
    cookies.remove('short');
    cookies.remove('user');
  }

  const isLoggedIn = computed(() => {
    return userData.value ? true : false;
  });

  const isOperationsUser = computed(() => {
    return userData.value?.role === 'operations';
  });

  const isBasicUser = computed(() => {
    // accepted: user
    return (
      userData.value?.role !== 'client_administrator' &&
      userData.value?.role !== 'staff' &&
      userData.value?.role !== 'admin' &&
      userData.value?.role !== 'super_admin'
    );
  });

  const isClientAdmin = computed(() => {
    return userData.value?.role === 'client_administrator';
  });

  const isNonStaff = computed(() => {
    // accepted: user, client_administrator
    return (
      userData.value?.role !== 'staff' &&
      userData.value?.role !== 'admin' &&
      userData.value?.role !== 'super_admin'
    );
  });

  const isStaff = computed(() => {
    return (
      userData.value?.role === 'staff' ||
      userData.value?.role === 'admin' ||
      userData.value?.role === 'super_admin'
    );
  });

  const isMeshhAdmin = computed(() => {
    return userData.value?.role === 'admin' || userData.value?.role === 'super_admin';
  });

  const hasScope = (scope: string) => {
    if (userData.value?.role === 'admin' || userData.value?.role === 'super_admin') {
      return true;
    }

    return userData?.value?.scopes?.includes(scope);
  };

  const hasFeature = (feature: string) => {
    if (userData.value?.role === 'admin' || userData.value?.role === 'super_admin') {
      return true;
    }

    return userData?.value?.features?.includes(feature);
  };

  return {
    accessToken,
    isLoggedIn,
    userData,
    demoEvent,
    demoTemplate,

    isBasicUser,
    isClientAdmin,
    isNonStaff,
    isStaff,
    isMeshhAdmin,
    isOperationsUser,
    hasScope,
    hasFeature,
    getInitials,
    getFirstName,
    logIn,
    restoreFromCookies,
    updateToken,
    logOut,
  };
});
